<script setup>
import { onMounted, reactive, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { notification } from 'ant-design-vue';
import DataTable from '@/components/common/DataTable.vue';
import TableIndexColumn from '@/components/common/TableIndexColumn.vue';
import usePagination from '@/composables/usePagination';
import DialogDelete from '@/views/permission/DialogDelete.vue';
import ModalEditRole from '@/views/permission/ModalEditRole.vue';
import ModalAddRole from '@/views/permission/ModalAddRole.vue';
import { usePermission } from '@/composables/usePermission';
import { ACTIVE_PERMISSION_MODULE_NAMES } from '@/constants/permissions';
import { deleteRole } from '@/api/roles/deleteRole';
import { assignPermissionsToRole } from '@/api/roles/assignPermissionsToRole';
import { roleList } from '@/api/roles/roleList';
import { permissionList } from '@/api/permissions/permissionList';

// 使用權限控制
const {
    canView,
    canCreate,
    canUpdate,
    canDelete,
    canOperate,
    availablePermissions
} = usePermission('RoleManagement');

// 表格資料
const tableData = ref([]);

// 權限資料
const permissionData = ref([]);

// 僅顯示仍上線的模組（過濾已下線的會員管理、課程管理等）
const visiblePermissionData = computed(() =>
    permissionData.value.filter((p) =>
        ACTIVE_PERMISSION_MODULE_NAMES.includes(p.name)
    )
);

//  當前選擇的權限資料
let currentPermissions = ref(new Set());

// 當前選擇的角色ID
let currentID = ref(null);

// 分頁設置
const { pagination, updatePagination, resetPagination } = usePagination({
    pageSize: 20
});

/**
 * 角色權限彈窗用：顯示「去掉頂層模組名」之後的路徑。
 * 舊版只用 split('.')[1]，會把「諮詢工作台.版本.檢視／建立／編輯」都顯示成「版本」。
 * 參數相關權限補上頁面對照，避免「兩個參數頁」在勾選列看不出差異。
 */
const PERMISSION_LEAF_ALIASES = {
    參數設置: '參數設置（簡版／可約區間）',
    開班表: '開班表（可排班日）',
    預約規則: '完整參數設置（工程師）'
};

function permissionLeafLabel(fullName) {
    if (!fullName || typeof fullName !== 'string') return '';
    const parts = fullName.split('.');
    if (parts.length < 2) return fullName;
    const leaf = parts.slice(1).join('．');
    return PERMISSION_LEAF_ALIASES[leaf] || leaf;
}

// 路由
const router = useRouter();

// 表格欄位定義
const columns = computed(() => {
    const baseColumns = [
        {
            title: '序號',
            key: 'index',
            width: 40,
            fixed: 'left'
        },
        {
            title: '名稱',
            key: 'name',
            dataIndex: 'name',
            width: 80
        },
        {
            title: '綁定數量',
            key: 'managers_count',
            dataIndex: 'managers_count',
            width: 80
        },
        {
            title: '權限種類',
            key: 'permissions',
            dataIndex: 'permissions',
            width: 200
        }
    ];

    // 只有當有編輯、刪除或權限分配權限時才添加操作欄位
    if (
        availablePermissions.value.update ||
        availablePermissions.value.delete ||
        canOperate.value
    ) {
        baseColumns.push({
            title: '操作',
            key: 'action',
            width: 80,
            fixed: 'right'
        });
    }

    return baseColumns;
});

// 獲取權限列表
const getAllPermissions = async (page = 1, pageSize = 0) => {
    try {
        const response = await permissionList(pageSize, page);
        console.log('權限列表 API 回應:', response);

        if (response.status === 1) {
            // API 已統一為格式 A：data 直接是陣列
            permissionData.value = Array.isArray(response.data) ? response.data : [];
        } else {
            notification.error({
                message: '獲取權限列表失敗',
                description: response.message || '請稍後再試'
            });
        }
    } catch (error) {
        console.error('獲取權限列表 API 錯誤:', error);
        notification.error({
            message: '獲取權限列表失敗',
            description: '網路連線錯誤，請稍後再試'
        });
    }
};

// 獲取角色列表
const getRoleList = async (page = 1, pageSize = 20) => {
    try {
        const response = await roleList(pageSize, page);
        console.log('角色列表 API 回應:', response);

        if (response.status === 1) {
            tableData.value = response.data || [];
            updatePagination({
                current: page,
                pageSize,
                total: response.data.total || 0
            });
        } else {
            notification.error({
                message: '獲取角色列表失敗',
                description: response.message || '請稍後再試'
            });
        }
    } catch (error) {
        console.error('獲取角色列表 API 錯誤:', error);
        notification.error({
            message: '獲取角色列表失敗',
            description: '網路連線錯誤，請稍後再試'
        });
    }
};

// 表格變化處理（DataTable 外部分頁：emit { pagination, filters, sorter }）
const handleTableChange = ({ pagination: pag }) => {
    if (!pag?.current) return;
    updatePagination(pag);
    getRoleList(pag.current, pag.pageSize);
};

// 刷新處理
const handleRefresh = async () => {
    await getRoleList(pagination.value.current, pagination.value.pageSize);
};

// 彈窗狀態管理
const showDialog = reactive({
    add: false,
    edit: false,
    delete: false,
    permission: false
});

// 當前操作的行資料
const rowData = ref({});

// 新增角色
const handleAdd = () => {
    showDialog.add = true;
};

// 當前操作的行資料
const deleteId = ref(null);
const deleteName = ref('');

// 刪除確認處理
const handleDeleteConfirm = async (isConfirm) => {
    if (isConfirm) {
        try {
            const response = await deleteRole(deleteId.value);

            if (response.status === 1) {
                notification.success({
                    message: '刪除成功',
                    description: '角色資料已成功刪除'
                });

                showDialog.delete = false;
                await getRoleList(1, pagination.value.pageSize);
            } else {
                notification.error({
                    message: '刪除失敗',
                    description: response.message || '請稍後再試'
                });
            }
        } catch (error) {
            console.error('刪除 API 錯誤:', error);
            notification.error({
                message: '刪除失敗',
                description: '網路連線錯誤，請稍後再試'
            });
        }
    } else {
        console.log('取消刪除操作');
    }
};

// 編輯
const handleEditAction = (record) => {
    showDialog.edit = true;
    rowData.value = JSON.parse(JSON.stringify(record));
};

// 刪除
const handleDelAction = (record) => {
    showDialog.delete = true;
    deleteId.value = record.id;
    deleteName.value = record.name;
};

// 編輯權限
const handlePermissionAction = (role_id, permissions) => {
    showDialog.permission = true;

    const arr = [];

    // 檢查 permissions 是否存在且為陣列
    if (permissions && Array.isArray(permissions)) {
        permissions.forEach((p) => {
            arr.push(p.id);

            // 檢查 p.data 是否存在且為陣列
            if (p.data && Array.isArray(p.data)) {
                p.data.forEach((pp) => {
                    arr.push(pp.id);
                    // 檢查 pp.data 是否存在且為陣列
                    if (
                        pp?.data &&
                        Array.isArray(pp.data) &&
                        pp.data.length > 0
                    ) {
                        pp.data.forEach((ppp) => {
                            arr.push(ppp.id);
                        });
                    }
                });
            }
        });
    }

    currentID.value = role_id;
    currentPermissions.value = new Set(arr);
};

// 選擇角色權限
const togglePermission = (id) => {
    currentPermissions.value.has(id)
        ? currentPermissions.value.delete(id)
        : currentPermissions.value.add(id);
};

// 取得某父層權限底下所有子孫節點的 ID（僅用於父層勾選時一次全選）
const getDescendantIds = (permission) => {
    const level2 = (permission?.data || []).map((child) => child.id);
    const level3 = (permission?.data || []).flatMap((child) =>
        (child?.data || []).map((grand) => grand.id)
    );
    return [...level2, ...level3];
};

// 父層「顯示在側邊欄」切換：
// - 勾選：父層 + 所有子孫全部加入
// - 取消：只移除父層，不動子孫（使用者可自行微調子項）
const onToggleTop = (permission, e) => {
    const isChecked = e?.target?.checked;
    if (isChecked) {
        currentPermissions.value.add(permission.id);
        getDescendantIds(permission).forEach((id) => {
            currentPermissions.value.add(id);
        });
    } else {
        currentPermissions.value.delete(permission.id);
    }
};

// 中層（父層）切換：
// - 勾選：父層 + 所有子層加入
// - 取消：父層 + 所有子層移除
const onToggleParent = (parent, e) => {
    const isChecked = e?.target?.checked;
    const children = parent?.data || [];
    if (isChecked) {
        currentPermissions.value.add(parent.id);
        children.forEach((child) => currentPermissions.value.add(child.id));
    } else {
        currentPermissions.value.delete(parent.id);
        children.forEach((child) => currentPermissions.value.delete(child.id));
    }
};

// 更新角色權限
const updateRolePermission = async () => {
    try {
        const response = await assignPermissionsToRole(currentID.value, [
            ...currentPermissions.value
        ]);

        if (response.status === 1) {
            notification.success({
                message: '更新成功',
                description: '角色權限資料已成功更新'
            });

            showDialog.permission = false;
            await getRoleList(1, pagination.value.pageSize);
        } else {
            notification.error({
                message: '更新失敗',
                description: response.message || '請稍後再試'
            });
        }
    } catch (error) {
        console.error('更新 API 錯誤:', error);
        notification.error({
            message: '更新失敗',
            description: '網路連線錯誤，請稍後再試'
        });
    }
};

// 依角色 ID 跳轉到管理者列表並帶入查詢參數
const goToAdminListByRole = (roleId) => {
    // 僅在角色 ID 存在時才進行跳轉
    if (!roleId) return;
    router.push({ name: 'AdminList', query: { role_id: String(roleId) } });
};

// 新增成功處理
const handleAddSuccess = async (data) => {
    showDialog.add = false;
    // 移除重複的通知，ModalAddRole 組件已經顯示了
    await getRoleList(1, pagination.value.pageSize);
};

// 編輯成功處理
const handleEditSuccess = async (data) => {
    showDialog.edit = false;
    // 移除重複的通知，ModalEditRole 組件已經顯示了
    await getRoleList(1, pagination.value.pageSize);
};

onMounted(() => {
    getRoleList(1, 20);
    getAllPermissions();
});
</script>

<template>
    <a-modal
        :width="950"
        v-model:open="showDialog.permission"
        title="編輯權限"
        wrap-class-name="edit-modal"
        okText="儲存"
        cancelText="取消"
        centered
        @ok="updateRolePermission"
    >
        <hr class="mt-4" />
        <div class="flex justify-between">
            <div
                class="mt-4 role_permission_list"
                v-for="permission in visiblePermissionData"
                :key="permission.id"
            >
                <b>{{ permission.name }} :</b>
                <label
                    class="flex items-center mt-2"
                    :for="`permission_ele_${permission.id}`"
                >
                    <input
                        class="mr-2"
                        :id="`permission_ele_${permission.id}`"
                        type="checkbox"
                        @change="onToggleTop(permission, $event)"
                        :checked="currentPermissions.has(permission.id)"
                    />
                    <span>顯示在側邊欄</span>
                </label>

                <div
                    class="ml-6"
                    v-for="child in permission.data || []"
                    :key="child.id"
                >
                    <label
                        class="flex items-center"
                        :for="`permission_ele_${child.id}`"
                    >
                        <input
                            class="mr-1"
                            :id="`permission_ele_${child.id}`"
                            type="checkbox"
                            @change="onToggleParent(child, $event)"
                            :checked="currentPermissions.has(child.id)"
                        />
                        <span>
                            {{ permissionLeafLabel(child.name) }}
                        </span>
                    </label>

                    <div class="ml-7" v-for="c in child.data || []" :key="c.id">
                        <label
                            class="flex items-center"
                            :for="`permission_ele_${c.id}`"
                            @change="togglePermission(c.id)"
                        >
                            <input
                                class="mr-1"
                                :id="`permission_ele_${c.id}`"
                                type="checkbox"
                                :checked="currentPermissions.has(c.id)"
                            />
                            <span>{{ permissionLeafLabel(c.name) }}</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    </a-modal>

    <ModalAddRole
        :open="showDialog.add"
        @update:open="showDialog.add = $event"
        @success="handleAddSuccess"
    />

    <ModalEditRole
        :open="showDialog.edit"
        :role-data="rowData"
        @update:open="showDialog.edit = $event"
        @success="handleEditSuccess"
    />

    <DialogDelete
        v-model:open="showDialog.delete"
        :role-name="deleteName"
        @confirm="handleDeleteConfirm"
    />

    <!-- 功能按鈕 -->
    <div class="flex justify-end mb-4">
        <a-space>
            <a-button
                v-if="availablePermissions.create"
                type="primary"
                @click="handleAdd"
            >
                新增
            </a-button>
        </a-space>
    </div>

    <DataTable
        title="列表"
        :columns="columns"
        :data-source="tableData"
        :pagination="pagination"
        :scroll="{ x: 1200 }"
        @refresh="handleRefresh"
        @change="handleTableChange"
    >
        <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'index'">
                <TableIndexColumn :index="index" :pagination="pagination" />
            </template>

            <template v-else-if="column.key === 'managers_count'">
                <a-button
                    v-if="Number(record.managers_count) > 0"
                    type="link"
                    size="small"
                    class="!p-0"
                    @click="goToAdminListByRole(record.id)"
                >
                    {{ record.managers_count }}
                </a-button>
                <span v-else>{{ record.managers_count }}</span>
            </template>

            <template v-else-if="column.key === 'permissions'">
                <div class="flex flex-wrap gap-x-2 gap-y-2">
                    <a-tag
                        v-for="(permission, i) in record.permissions.filter((p) =>
                            ACTIVE_PERMISSION_MODULE_NAMES.includes(p.name)
                        )"
                        :key="i"
                        class="m-0"
                    >
                        {{ permission.name }}
                    </a-tag>
                </div>
            </template>

            <template v-else-if="column.key === 'action'">
                <template
                    v-if="
                        availablePermissions.update ||
                        availablePermissions.delete ||
                        canOperate
                    "
                >
                    <a-button
                        v-if="availablePermissions.update"
                        type="text"
                        size="small"
                        class="min-w-[50px] !text-blue-500 hover:!text-blue-600"
                        @click="handleEditAction(record)"
                    >
                        編輯
                    </a-button>

                    <a-button
                        v-if="availablePermissions.delete"
                        type="text"
                        size="small"
                        class="min-w-[50px] !text-red-500 hover:!text-red-600"
                        @click="handleDelAction(record)"
                    >
                        刪除
                    </a-button>
                    <a-button
                        v-if="canOperate"
                        type="text"
                        size="small"
                        class="min-w-[50px] !text-green-700 hover:!text-green-700"
                        @click="
                            handlePermissionAction(
                                record.id,
                                record.permissions
                            )
                        "
                    >
                        權限
                    </a-button>
                </template>
                <template v-else>
                    <a-tag color="default">僅查看</a-tag>
                </template>
            </template>
        </template>
    </DataTable>
</template>

<style lang="css">
.role_permission_list input[type='checkbox'] {
    position: relative;
    border: 1.5px solid #000;
    border-radius: 4px;
    background-color: transparent;
    cursor: pointer;
    height: 16px;
    width: 16px;
    appearance: none; /* 增加標準屬性以改善相容性 */
    -webkit-appearance: none;
    opacity: 0.4;
    isolation: isolate;
}

.role_permission_list input[type='checkbox']:focus {
    outline: none;
}

.role_permission_list label:hover,
.role_permission_list input[type='checkbox']:hover {
    opacity: 0.8;
    cursor: pointer;
    color: #7f36f5;
    border-color: #7f36f5;
}

.role_permission_list input[type='checkbox']:checked {
    opacity: 1;
    background-color: #7f36f5;
    border-color: #7f36f5;
}

.role_permission_list input[type='checkbox']:checked ~ span {
    color: #7f36f5;
}

.role_permission_list input[type='checkbox']:before {
    content: '';
    position: absolute;
    right: 50%;
    top: 50%;
    width: 5px;
    height: 9px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    margin: -1px -1px 0 -1px;
    transform: rotate(45deg) translate(-50%, -50%);
    z-index: 2;
}
</style>
